import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { environment } from '../core/environment';

interface Lookup { id: string; name: string; }
interface StaffRow { id: string; staffCode: string; name: string; roleName: string; mobile: string; email?: string; designationId: string; assignedSportId?: string; status: number; }

@Component({
  selector: 'app-staff-management',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <div class="ops-page">
      <div class="ops-hero staff-hero">
        <div><span>Team Operations</span><h2>Staff Management</h2><p>Add coaches, receptionists and support staff with sport assignment and shift timing.</p></div>
        <button type="button" (click)="load()">Refresh Staff</button>
      </div>

      <section class="ops-stats">
        <article><span>Total Staff</span><strong>{{ staff().length }}</strong><p>All active records</p></article>
        <article><span>Coaches</span><strong>{{ roleCount('Coach') }}</strong><p>Training team</p></article>
        <article><span>Reception</span><strong>{{ roleCount('Receptionist') }}</strong><p>Front desk team</p></article>
        <article><span>Active</span><strong>{{ activeCount() }}</strong><p>Available for shifts</p></article>
      </section>

      <section class="ops-grid">
        <form class="ops-card ops-form" [formGroup]="form" (ngSubmit)="create()">
          <div class="card-title"><div><h3>Add Staff</h3><p>Create staff profile and assignment.</p></div><span>Profile</span></div>
          <label>Name <input formControlName="name"></label>
          <div class="two-col">
            <label>Role
              <select formControlName="roleName">
                <option>Coach</option><option>Receptionist</option><option>Admin</option><option>Staff</option>
              </select>
            </label>
            <label>Designation
              <select formControlName="designationId">
                @for (designation of designations(); track designation.id) {
                  <option [value]="designation.id">{{ designation.name }}</option>
                }
              </select>
            </label>
          </div>
          <div class="two-col">
            <label>Mobile <input formControlName="mobile"></label>
            <label>Email <input formControlName="email"></label>
          </div>
          <label>Assigned Sport
            <select formControlName="assignedSportId">
              <option [ngValue]="null">All / General</option>
              @for (sport of sports(); track sport.id) {
                <option [value]="sport.id">{{ sport.name }}</option>
              }
            </select>
          </label>
          <div class="two-col">
            <label>Shift Start <input type="time" formControlName="shiftStartTime"></label>
            <label>Shift End <input type="time" formControlName="shiftEndTime"></label>
          </div>
          <button class="ops-primary" type="submit" [disabled]="form.invalid">Add Staff</button>
        </form>

        <article class="ops-card">
          <div class="card-title"><div><h3>Staff Directory</h3><p>Coach and admin team details.</p></div></div>
          <table>
            <thead><tr><th>Code</th><th>Name</th><th>Role</th><th>Mobile</th><th>Status</th></tr></thead>
            <tbody>
              @for (member of staff(); track member.id) {
                <tr>
                  <td>{{ member.staffCode }}</td><td>{{ member.name }}</td><td>{{ member.roleName }}</td><td>{{ member.mobile }}</td>
                  <td><span class="status-chip">{{ member.status === 1 ? 'Active' : 'Inactive' }}</span></td>
                </tr>
              }
            </tbody>
          </table>
        </article>
      </section>
    </div>
  `
})
export class StaffManagementComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(FormBuilder);
  readonly staff = signal<StaffRow[]>([]);
  readonly sports = signal<Lookup[]>([]);
  readonly designations = signal<Lookup[]>([]);
  readonly form = this.fb.group({
    name: ['', Validators.required],
    roleName: ['Coach', Validators.required],
    mobile: ['', Validators.required],
    email: [''],
    designationId: ['', Validators.required],
    assignedSportId: [null as string | null],
    shiftStartTime: ['09:00:00'],
    shiftEndTime: ['18:00:00']
  });

  ngOnInit(): void {
    this.http.get<Lookup[]>(`${environment.apiUrl}/masters/sports`).subscribe((x) => this.sports.set(x));
    this.http.get<Lookup[]>(`${environment.apiUrl}/masters/staff-designations`).subscribe((x) => {
      this.designations.set(x);
      this.form.patchValue({ designationId: x[0]?.id ?? '' });
    });
    this.load();
  }

  roleCount(role: string): number { return this.staff().filter((x) => x.roleName === role).length; }
  activeCount(): number { return this.staff().filter((x) => x.status === 1).length; }
  load(): void { this.http.get<StaffRow[]>(`${environment.apiUrl}/staff`).subscribe((x) => this.staff.set(x)); }
  create(): void {
    if (this.form.invalid) return;
    this.http.post(`${environment.apiUrl}/staff`, this.form.getRawValue()).subscribe(() => {
      this.form.patchValue({ name: '', mobile: '', email: '' });
      this.load();
    });
  }
}
